"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Dialog = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _inheritsLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/inheritsLoose"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _reactDom = _interopRequireDefault(require("react-dom"));
var _constants = require("../../constants");
var _customPropTypes = _interopRequireDefault(require("../../util/custom-prop-types"));
var _dom = require("../../util/dom");
var _focus = require("../../util/focus");
var _identifier = require("../../util/identifier");
var _logging = require("../../util/logging");
var _CloseButton = require("../CloseButton/CloseButton");
var _DialogBody = require("../DialogBody/DialogBody");
var _DialogClose = require("../DialogClose/DialogClose");
var _DialogFooter = require("../DialogFooter/DialogFooter");
var _DialogHeader = require("../DialogHeader/DialogHeader");
var _DialogLayout = require("../DialogLayout/DialogLayout");
var _Dialog = require("./Dialog.styles");
var _DialogEventTarget = require("./DialogEventTarget");
var _excluded = ["body", "children", "closeButton", "eventTarget", "footer", "header", "innerRef", "isOpen", "onBeforeTakeFocus", "onClose", "onCloseButtonClick", "onESCKeyPress", "onOutsideClick", "portal", "renderLayout", "renderHeader", "renderBody", "renderFooter", "renderCloseButton", "shouldCloseOnESCKeyPress", "shouldCloseOnOutsideClick", "shouldReturnFocus", "shouldTakeFocus", "shouldTrapFocus"];
var DialogComponent = /*#__PURE__*/function (_Component) {
  (0, _inheritsLoose2.default)(DialogComponent, _Component);
  function DialogComponent(props) {
    var _this;
    _this = _Component.call(this, props) || this;
    _this.setup = function () {
      // Yield the thread before initializing new event listeners to allow existing event
      // listeners to be handled first.
      // This is required because React's synthetic events fire before native events,
      // which can result in a new dialog being initialized and stealing the event of an already
      // open dialog, causing it to close immediately.
      setTimeout(function () {
        _this.addEventListeners();
      }, 0);
      if (_this.props.triggerRef) {
        _this.trigger = _this.props.triggerRef.current;
      } else if (typeof document !== 'undefined' && document.activeElement !== document.body) {
        // Capture the triggering element, ignoring body which is the default when there is no
        // focus.
        _this.trigger = document.activeElement;
      }
      if (_this.props.shouldTakeFocus && _this.ref.current) {
        if (_this.props.onBeforeTakeFocus) {
          _this.props.onBeforeTakeFocus();
        }
        _this.ref.current.focus();
      }
      var node = _this.bodyRef.current;
      _this.setState({
        isBodyScrollable: node && node.scrollHeight > node.clientHeight && _this.props.isOpen
      });
    };
    _this.teardown = function () {
      delete _this.mouseDownTarget;
      delete _this.mouseUpTarget;
      _this.removeEventListeners();
      if (_this.shouldReturnFocus()) {
        _this.trigger.focus({
          preventScroll: _this.props.shouldPreventScrollOnFocusReturn
        });
      }
    };
    _this.shouldReturnFocus = function () {
      if (!_this.trigger || !_this.props.shouldReturnFocus) {
        return false;
      }

      // It's safe to return focus if there is no focus at all, or if the focus is currently on
      // the document body (focus goes here when clicking in dead space).
      if (!document.activeElement || document.activeElement === document.body) {
        return true;
      }

      // If the focus is within the dialog, then it is safe to return focus,
      // otherwise we leave focus where it is.
      return (0, _dom.hasAncestor)(document.activeElement, _this.ref.current);
    };
    _this.addEventListeners = function () {
      _this.props.eventTarget.addEventListener('click', _this.onDocumentClick);
      _this.props.eventTarget.addEventListener('keydown', _this.onKeyDown);
      _this.props.eventTarget.addEventListener('mousedown', _this.onMouseDown);
      _this.props.eventTarget.addEventListener('mouseup', _this.onMouseUp);
    };
    _this.removeEventListeners = function () {
      _this.props.eventTarget.removeEventListener('mousedown', _this.onMouseDown);
      _this.props.eventTarget.removeEventListener('mouseup', _this.onMouseUp);
      _this.props.eventTarget.removeEventListener('click', _this.onDocumentClick);
      _this.props.eventTarget.removeEventListener('keydown', _this.onKeyDown);
    };
    _this.onMouseDown = function (e) {
      _this.mouseDownTarget = e.target;
    };
    _this.onMouseUp = function (e) {
      _this.mouseUpTarget = e.target;
    };
    _this.onDocumentClick = function (e) {
      if (_this.props.isOpen &&
      // Make sure we have a mouse target because Space and Enter can trigger "click"
      _this.mouseDownTarget && _this.mouseUpTarget &&
      // The click did not originate within the modal dialog
      !(0, _dom.hasAncestor)(_this.mouseDownTarget, _this.ref.current) &&
      // The click did not terminate within the modal dialog
      !(0, _dom.hasAncestor)(_this.mouseUpTarget, _this.ref.current) &&
      // An orphan can happen when you click on the close button on a dialog and it removes
      // the node before the document click registers.
      !(0, _dom.isOrphan)(e.target)) {
        // Make sure the outside click should not be ignored
        if (_this.props.shouldIgnoreTriggerClick && _this.trigger && (
        // The click originated within the trigger
        (0, _dom.hasAncestor)(_this.mouseDownTarget, _this.trigger) ||
        // The click terminated within the trigger
        (0, _dom.hasAncestor)(_this.mouseUpTarget, _this.trigger))) {
          return;
        }

        // Attach some additional event targets to the event
        e.mouseDownTarget = _this.mouseDownTarget;
        e.mouseUpTarget = _this.mouseUpTarget;
        _this.props.onOutsideClick(e);
        if (_this.props.shouldCloseOnOutsideClick) {
          _this.props.onClose();
        }
      }
    };
    _this.onKeyDown = function (e) {
      // Handle ESC key
      if (e.keyCode === _constants.KEY_CODES.ESC && _this.props.isOpen) {
        _this.props.onESCKeyPress();
        if (_this.props.shouldCloseOnESCKeyPress) {
          _this.props.onClose();
        }
      }

      // Handle TAB key
      if (_this.props.shouldTrapFocus && e.keyCode === _constants.KEY_CODES.TAB && typeof document !== 'undefined' && (0, _dom.hasAncestor)(document.activeElement, _this.ref.current)) {
        (0, _focus.trapTabEvent)(_this.ref.current, e);
      }
    };
    _this.onCloseButtonClick = function () {
      _this.props.onCloseButtonClick();
      _this.props.onClose();
    };
    _this.ariaLabel = (0, _identifier.identifier)();
    _this.ariaDesc = (0, _identifier.identifier)();
    _this.ref = props.innerRef || /*#__PURE__*/(0, _react.createRef)();
    _this.bodyRef = /*#__PURE__*/(0, _react.createRef)();
    _this.state = {
      isBodyScrollable: false
    };
    return _this;
  }
  var _proto = DialogComponent.prototype;
  _proto.componentDidMount = function componentDidMount() {
    if (this.props.isOpen) {
      this.setup();
    }
  };
  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen) {
      if (this.props.isOpen) {
        // On opened
        this.setup();
      } else {
        // On closed
        this.teardown();
      }
    }
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    this.teardown();
  };
  _proto.render = function render() {
    var _this$props = this.props,
      body = _this$props.body,
      children = _this$props.children,
      closeButton = _this$props.closeButton,
      eventTarget = _this$props.eventTarget,
      footer = _this$props.footer,
      header = _this$props.header,
      innerRef = _this$props.innerRef,
      isOpen = _this$props.isOpen,
      onBeforeTakeFocus = _this$props.onBeforeTakeFocus,
      onClose = _this$props.onClose,
      onCloseButtonClick = _this$props.onCloseButtonClick,
      onESCKeyPress = _this$props.onESCKeyPress,
      onOutsideClick = _this$props.onOutsideClick,
      portal = _this$props.portal,
      renderLayout = _this$props.renderLayout,
      renderHeader = _this$props.renderHeader,
      renderBody = _this$props.renderBody,
      renderFooter = _this$props.renderFooter,
      renderCloseButton = _this$props.renderCloseButton,
      shouldCloseOnESCKeyPress = _this$props.shouldCloseOnESCKeyPress,
      shouldCloseOnOutsideClick = _this$props.shouldCloseOnOutsideClick,
      shouldReturnFocus = _this$props.shouldReturnFocus,
      shouldTakeFocus = _this$props.shouldTakeFocus,
      shouldTrapFocus = _this$props.shouldTrapFocus,
      rest = (0, _objectWithoutProperties2.default)(_this$props, _excluded);

    // Do not render anything while the dialog is closed
    if (!isOpen) {
      return null;
    }
    var dialogProps = {};
    var titleProps = {
      id: this.ariaLabel
    };
    var descProps = {
      id: this.ariaDesc
    };
    var closeButtonProps = {
      onClick: this.onCloseButtonClick
    };
    if (header || renderHeader || typeof children === 'function') {
      dialogProps['aria-labelledby'] = this.ariaLabel;
    }
    if (body || renderBody || typeof children === 'function') {
      dialogProps['aria-describedby'] = this.ariaDesc;
    }
    if (header && renderHeader) {
      (0, _logging.log)('Do not use `header` and `renderHeader` at the same time, use `renderHeader`.');
    }
    if (body && renderBody) {
      (0, _logging.log)('Do not use `body` and `renderBody` at the same time, use `renderBody`.');
    }
    if (footer && renderFooter) {
      (0, _logging.log)('Do not use `footer` and `renderFooter` at the same time, use `renderFooter`.');
    }
    var childrenSection = children;
    if (typeof children === 'function') {
      childrenSection = children({
        titleProps: titleProps,
        descProps: descProps
      });
    }
    var headerSection = renderHeader || header;
    if (typeof renderHeader === 'function') {
      headerSection = renderHeader(Object.assign({}, titleProps), Object.assign({}, this.props));
    } else if (headerSection) {
      headerSection = /*#__PURE__*/_react.default.createElement(_DialogHeader.DialogHeader, titleProps, headerSection);
    }
    var bodySection = renderBody || body;
    var bodyProps = this.state.isBodyScrollable ? {
      tabIndex: 0,
      ref: this.bodyRef
    } : {
      ref: this.bodyRef
    };
    if (typeof renderBody === 'function') {
      bodySection = renderBody(Object.assign({}, descProps), Object.assign({}, this.props, bodyProps));
    } else if (bodySection) {
      bodySection = /*#__PURE__*/_react.default.createElement(_DialogBody.DialogBody, (0, _extends2.default)({}, descProps, bodyProps), bodySection);
    }
    var footerSection = renderFooter || footer;
    if (typeof renderFooter === 'function') {
      footerSection = renderFooter({}, Object.assign({}, this.props));
    } else if (footerSection) {
      footerSection = /*#__PURE__*/_react.default.createElement(_DialogFooter.DialogFooter, null, footerSection);
    }
    var closeSection = renderCloseButton || closeButton;
    if (typeof renderCloseButton === 'undefined' && typeof closeButton === 'undefined') {
      closeSection = /*#__PURE__*/_react.default.createElement(_CloseButton.CloseButton, null);
    }
    if (typeof closeSection === 'function') {
      closeSection = renderCloseButton({}, Object.assign({}, this.props), Object.assign({}, closeButtonProps));
    } else if (closeSection) {
      closeSection = /*#__PURE__*/_react.default.createElement(_DialogClose.DialogClose, null, /*#__PURE__*/(0, _react.cloneElement)(closeSection, Object.assign({}, closeButtonProps)));
    }
    var allSections = /*#__PURE__*/_react.default.createElement(_react.Fragment, null, headerSection, bodySection, childrenSection, footerSection, closeSection);
    var layoutSections = {
      default: allSections,
      header: headerSection,
      body: bodySection,
      children: childrenSection,
      footer: footerSection,
      close: closeSection,
      layout: _DialogLayout.DialogLayout
    };
    var layoutSection = allSections;
    if (typeof renderLayout === 'function') {
      layoutSection = renderLayout({}, Object.assign({}, this.props), Object.assign({}, layoutSections));
    }
    var content = /*#__PURE__*/_react.default.createElement(_Dialog.StyledDialog, (0, _extends2.default)({
      ref: this.ref,
      tabIndex: "-1"
    }, dialogProps, rest), layoutSection);
    if (portal === true && typeof document !== 'undefined') {
      return /*#__PURE__*/_reactDom.default.createPortal(content, document.body);
    }
    if (portal !== true && portal) {
      return /*#__PURE__*/_reactDom.default.createPortal(content, portal);
    }
    return content;
  };
  return DialogComponent;
}(_react.Component);
/**
 * The `Dialog` component is the base for many messaging components
 * like `ModalDialog`, `Popover`, `Dropdown`, and `Menu`.
 *
 * @related [`ModalDialog`, `Popover`, `Dropdown`, `Menu`]
 * @since 5.0.0
 */
var Dialog = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  return /*#__PURE__*/_react.default.createElement(DialogComponent, (0, _extends2.default)({
    innerRef: forwardedRef || undefined
  }, props));
});
exports.Dialog = Dialog;
Dialog.displayName = 'Dialog';
Dialog.propTypes = {
  /**
   * @deprecated Use `renderBody` prop
   */
  body: _propTypes.default.node,
  /**
   * An alternative to using the `header`, `body`, and `footer` props, you can pass content
   * directly to `children` as a node or as a render function.
   * The downside of using this method is that there are no aria attributes provided by default.
   * When used in conjunction with any of `header`, `body`, or `footer`,
   * the content will be placed after `header` and `body`, and before `footer`.
   *
   * The render function receives an object with properties `titleProps` and `descProps` that
   * you can spread into your title and description nodes for aria support.
   */
  children: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
  /**
   * @deprecated Use `renderCloseButton` prop.
   */
  closeButton: _propTypes.default.node,
  /**
   * By default, dialogs use a custom
   * [EventTarget](https://developer.mozilla.org/en-US/docs/Web/API/EventTarget)
   * where listeners are only called for the latest dialog (to support stacking).
   * You can remove the custom behavior by setting this to the `document` object.
   */
  eventTarget: _propTypes.default.shape({
    addEventListener: _propTypes.default.func,
    removeEventListener: _propTypes.default.func
  }),
  /**
   * @deprecated Use `renderFooter` prop.
   */
  footer: _propTypes.default.node,
  /**
   * @deprecated Use `renderHeader` prop.
   */
  header: _propTypes.default.node,
  /**
   * @ignore
   */
  innerRef: _customPropTypes.default.ref,
  /**
   * Dialogs are controlled components where the `isOpen` prop controls the visibility of the
   * dialog.
   */
  isOpen: _propTypes.default.bool,
  /**
   * Function called before focus is initially moved.
   * You can use this to save a reference of the activeElement before it moves.
   */
  onBeforeTakeFocus: _propTypes.default.func,
  /**
   * Function called when the close button is clicked, outside the notification is clicked,
   * or the ESC key is pressed.
   *
   * Use the `shouldCloseOnEscKeyPress` and `shouldCloseOnOutsideClick` props to
   * customize this behavior.
   */
  onClose: _propTypes.default.func,
  /**
   * Function called when the close button is clicked.
   * This function is called before `onClose`.
   */
  onCloseButtonClick: _propTypes.default.func,
  /**
   * Function called when the ESC key is pressed.
   * This function is called before `onClose`.
   */
  onESCKeyPress: _propTypes.default.func,
  /**
   * Function called when there is a click outside of the dialog. The function will receive the
   * document click event as the only argument, with `mouseDownTarget` and `mouseUpTarget` also
   * available on the event. This function is called before `onClose`.
   */
  onOutsideClick: _propTypes.default.func,
  /**
   * By default, dialogs will be rendered into a [Portal](https://reactjs.org/docs/portals.html)
   * in the document body. With this prop, you can specify a different container,
   * or you can disable the portal to render the content inline by passing `false`.
   */
  portal: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.bool]),
  /**
   * A function that receives `props`, `rootProps`, and `options` as arguments. `options` contain
   * `default` layout, and `header`, `body`, `children`, and `footer` sections.
   *
   * @since 8.54.0
   */
  renderLayout: _propTypes.default.func,
  /**
   * The header content for the dialog, wrapped in a `DialogHeader` component with
   * `aria-labelledby` assigned to the wrapper. Or a function that receives `props` and
   * `rootProps` as arguments. `props` contain `id` to match auto-generated `aria-labelledby`.
   *
   * @since 8.54.0
   */
  renderHeader: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
  /**
   * The body content for the dialog, wrapped in a `DialogBody` component with
   * `aria-describedby` assigned to the wrapper. Or a function that receives `props` and
   * `rootProps` as arguments. `props` contain `id` to match auto-generated `aria-describedby`.
   *
   * @since 8.54.0
   */
  renderBody: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
  /**
   * The footer content for the dialog, wrapped in a `DialogFooter` component. Or a function that
   * receives `props` and `rootProps` as arguments.
   *
   * @since 8.54.0
   */
  renderFooter: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
  /**
   * The close button for the dialog, wrapped in a `DialogClose` component. Or a function that
   * receives `props`, `rootProps`, and `closeButtonProps` as arguments. `closeButtonProps`
   * contains `onClick` event handler.
   *
   * By default uses the `CloseButton` component that satisfies
   * all accessibility requirements.
   *
   * @since 8.54.0
   */
  renderCloseButton: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
  /**
   * Dialogs are set to `role="dialog"` by default. Read more about the dialog role:
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
   */
  role: _propTypes.default.string,
  /**
   * When set to true, `onClose` will be called when the `ESC` key is pressed.
   */
  shouldCloseOnESCKeyPress: _propTypes.default.bool,
  /**
   * When set to true, `onClose` will be called when there is a click outside of the dialog.
   */
  shouldCloseOnOutsideClick: _propTypes.default.bool,
  /**
   * By default, clicking on the trigger is considered "outside" the dialog and will cause
   * `onOutsideClick` to be invoked. Setting this to true will ignore clicks on the trigger while
   * the dialog is open.
   *
   * @since 8.25.0
   */
  shouldIgnoreTriggerClick: _propTypes.default.bool,
  /**
   * Returns the focus to the trigger element when closed. More than likely,
   * you should never change this as it is an important feature for accessible dialogs.
   *
   * https://www.w3.org/TR/wai-aria-practices/#dialog_modal
   */
  shouldReturnFocus: _propTypes.default.bool,
  /**
   * Sets the focus on the dialog when opened. More than likely, you should never change this as
   * it is an important feature for accessible dialogs.
   *
   * https://www.w3.org/TR/wai-aria-practices/#dialog_modal
   */
  shouldTakeFocus: _propTypes.default.bool,
  /**
   * Traps the focus within the dialog when tabbing through elements. More than likely, you should
   * never change this as it is an important feature for accessible dialogs.
   *
   * https://www.w3.org/TR/wai-aria-practices/#dialog_modal
   */
  shouldTrapFocus: _propTypes.default.bool,
  /**
   * Prevents scrolling the document when focus is returned to the trigger element.
   * @since 8.101.3
   */
  shouldPreventScrollOnFocusReturn: _propTypes.default.bool,
  /**
   * The ref of the trigger used when `shouldReturnFocus` or `shouldIgnoreTriggerClick` is true.
   *
   * If this is not provided, the `document.activeElement` at the time of open will be used.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  triggerRef: _propTypes.default.object
};
Dialog.defaultProps = {
  eventTarget: new _DialogEventTarget.DialogEventTarget(),
  innerRef: null,
  isOpen: true,
  onBeforeTakeFocus: undefined,
  onClose: function onClose() {},
  onCloseButtonClick: function onCloseButtonClick() {},
  onESCKeyPress: function onESCKeyPress() {},
  onOutsideClick: function onOutsideClick() {},
  portal: true,
  role: 'dialog',
  shouldCloseOnESCKeyPress: false,
  shouldCloseOnOutsideClick: false,
  shouldIgnoreTriggerClick: false,
  shouldReturnFocus: true,
  shouldTakeFocus: true,
  shouldTrapFocus: true,
  shouldPreventScrollOnFocusReturn: true
};

// This makes eslint happy again when forwarding refs
DialogComponent.propTypes = process.env.NODE_ENV !== "production" ? Dialog.propTypes : {};
DialogComponent.defaultProps = Dialog.defaultProps;